import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import { getVisa, accetpVisa, refuseVisa } from "helpers/requests";
import Swal from "sweetalert2";
import { resolveImage, API_FILES } from "helpers/config";
import { format } from "date-fns";

const statusConfig = {
  PENDING: { color: "warning", label: "Pending", icon: "mdi mdi-clock-outline" },
  ACCEPTED: { color: "success", label: "Accepted", icon: "mdi mdi-check-circle" },
  REFUSED: { color: "danger", label: "Refused", icon: "mdi mdi-close-circle" },
};

const InfoItem = ({ label, value, icon, colSize = 6 }) => (
  <Col md={colSize} className="mb-3">
    <div style={{ fontSize: 11, color: "#74788d", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>
      {icon && <i className={`${icon} me-1`} />}
      {label}
    </div>
    <div style={{ fontSize: 15, fontWeight: 500, color: "#495057" }}>{value || "—"}</div>
  </Col>
);

const fmtDate = (d) => {
  if (!d) return "—";
  try { return format(new Date(d), "dd MMMM yyyy"); } catch { return "—"; }
};

const VisaDetails = ({ t }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [visa, setVisa] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetchVisa = async () => {
      try {
        const response = await getVisa(id);
        setVisa(response);
        setUser(response.user || null);
      } catch (error) {
        console.error("Error fetching visa:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchVisa();
  }, [id]);

  const handleAccept = async () => {
    const result = await Swal.fire({
      title: "Accept this visa?",
      text: "An acceptance email will be sent to the applicant.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#34c38f",
      confirmButtonText: "Yes, accept",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    setActionLoading(true);
    try {
      await accetpVisa(id, { email: user?.email, firstname: user?.firstname, lastname: user?.lastname });
      setVisa((prev) => ({ ...prev, status: "ACCEPTED" }));
      Swal.fire({ title: "Accepted!", text: "Visa has been accepted.", icon: "success" });
    } catch {
      Swal.fire({ title: "Error", text: "Failed to accept visa.", icon: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefuse = async () => {
    const result = await Swal.fire({
      title: "Refuse this visa?",
      text: "A rejection email will be sent to the applicant.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f46a6a",
      confirmButtonText: "Yes, refuse",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    setActionLoading(true);
    try {
      await refuseVisa(id, { email: user?.email, firstname: user?.firstname, lastname: user?.lastname });
      setVisa((prev) => ({ ...prev, status: "REFUSED" }));
      Swal.fire({ title: "Refused!", text: "Visa has been refused.", icon: "success" });
    } catch {
      Swal.fire({ title: "Error", text: "Failed to refuse visa.", icon: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  if (!visa) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={t("VISA")} breadcrumbItem={t("Details")} />
          <Card><CardBody className="text-center py-5"><h5>Visa not found</h5></CardBody></Card>
        </Container>
      </div>
    );
  }

  const status = statusConfig[visa.status] || statusConfig.PENDING;

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title={t("VISA")} breadcrumbItem={t("Visa Details")} />

        {/* Status + Actions Header */}
        <Card className="mb-3">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: 48, height: 48, borderRadius: 12,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    backgroundColor: status.color === "success" ? "#34c38f15" : status.color === "danger" ? "#f46a6a15" : "#f1b44c15",
                  }}
                >
                  <i className={`${status.icon} font-size-24`} style={{ color: status.color === "success" ? "#34c38f" : status.color === "danger" ? "#f46a6a" : "#f1b44c" }} />
                </div>
                <div>
                  <h5 className="mb-0">{visa.applicantName} {visa.applicantSurname}</h5>
                  <span className="text-muted font-size-13">{visa.country} — {visa.visaType} Visa</span>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Badge color={status.color} className="font-size-12 px-3 py-2">
                  <i className={`${status.icon} me-1`} />
                  {status.label}
                </Badge>
                {visa.status === "PENDING" && !actionLoading && (
                  <>
                    <Button color="success" size="sm" onClick={handleAccept} className="px-3">
                      <i className="mdi mdi-check me-1" /> Accept
                    </Button>
                    <Button color="danger" size="sm" onClick={handleRefuse} className="px-3">
                      <i className="mdi mdi-close me-1" /> Refuse
                    </Button>
                  </>
                )}
                {actionLoading && <Spinner size="sm" color="primary" />}
                <Button color="light" size="sm" onClick={() => navigate("/visa")} className="px-3">
                  <i className="mdi mdi-arrow-left me-1" /> Back
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        <Row>
          {/* Left Column: Visa & Applicant Info */}
          <Col lg={8}>
            {/* Visa Criteria */}
            <Card className="mb-3">
              <CardBody>
                <h5 className="mb-3">
                  <i className="mdi mdi-file-document-outline me-2 text-primary" />
                  Visa Criteria
                </h5>
                <Row>
                  <InfoItem label="Country" value={visa.country} icon="mdi mdi-earth" colSize={3} />
                  <InfoItem label="Visa Type" value={visa.visaType} icon="mdi mdi-passport" colSize={3} />
                  <InfoItem label="Travel Start Date" value={fmtDate(visa.startDate)} icon="mdi mdi-calendar" colSize={3} />
                  <InfoItem label="Nationality" value={visa.nationality} icon="mdi mdi-flag" colSize={3} />
                  <InfoItem label="Travel Document" value={visa.travelDocument} icon="mdi mdi-card-account-details" colSize={3} />
                  <InfoItem label="No. of Persons" value={visa.numPersons} icon="mdi mdi-account-group" colSize={3} />
                  <InfoItem label="Total Price" value={`${visa.totalPrice?.toLocaleString()} DA`} icon="mdi mdi-cash" colSize={3} />
                  <InfoItem label="Payment" value={visa.ispayed ? "Paid" : "Not Paid"} icon={visa.ispayed ? "mdi mdi-check-circle text-success" : "mdi mdi-clock-outline text-warning"} colSize={3} />
                </Row>
              </CardBody>
            </Card>

            {/* Applicant Details */}
            <Card className="mb-3">
              <CardBody>
                <h5 className="mb-3">
                  <i className="mdi mdi-account-outline me-2 text-primary" />
                  Applicant Details
                </h5>
                <Row>
                  <InfoItem label="First Name" value={visa.applicantName} icon="mdi mdi-account" />
                  <InfoItem label="Last Name" value={visa.applicantSurname} icon="mdi mdi-account" />
                  <InfoItem label="Place of Birth" value={visa.birthPlace} icon="mdi mdi-map-marker" />
                  <InfoItem label="Date of Birth" value={fmtDate(visa.birthDate)} icon="mdi mdi-cake-variant" />
                  <InfoItem label="Passport Number" value={visa.passportNumber} icon="mdi mdi-passport" />
                  <InfoItem label="Passport Expiration" value={fmtDate(visa.expirationDat)} icon="mdi mdi-calendar-alert" />
                  <InfoItem label="Email" value={visa.emailAddress} icon="mdi mdi-email" colSize={12} />
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Right Column: Documents + User Info */}
          <Col lg={4}>
            {/* Documents */}
            <Card className="mb-3">
              <CardBody>
                <h5 className="mb-3">
                  <i className="mdi mdi-folder-outline me-2 text-primary" />
                  Documents
                </h5>
                {[
                  { label: "Passport Scan", file: visa.passportScan, icon: "mdi mdi-passport" },
                  { label: "Photo", file: visa.photoScan, icon: "mdi mdi-camera" },
                  { label: "Birth Certificate", file: visa.birthCertScan, icon: "mdi mdi-file-certificate" },
                ].map((doc, i) => (
                  <div key={i} className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: i < 2 ? "1px solid #f0f0f0" : "none" }}>
                    <div className="d-flex align-items-center gap-2">
                      <i className={`${doc.icon} font-size-16 text-muted`} />
                      <span className="font-size-13">{doc.label}</span>
                    </div>
                    {doc.file ? (
                      <div className="d-flex gap-1">
                        <a
                          href={resolveImage(doc.file)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-soft-primary"
                        >
                          <i className="mdi mdi-eye" />
                        </a>
                        <a
                          href={API_FILES + doc.file}
                          download
                          className="btn btn-sm btn-soft-secondary"
                        >
                          <i className="mdi mdi-download" />
                        </a>
                      </div>
                    ) : (
                      <Badge color="light" className="text-muted">Not uploaded</Badge>
                    )}
                  </div>
                ))}
              </CardBody>
            </Card>

            {/* User Info */}
            {user && (
              <Card className="mb-3">
                <CardBody>
                  <h5 className="mb-3">
                    <i className="mdi mdi-account-circle-outline me-2 text-primary" />
                    Applicant Account
                  </h5>
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div
                      style={{
                        width: 44, height: 44, borderRadius: "50%",
                        backgroundColor: "#556ee615", display: "flex",
                        alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <i className="mdi mdi-account font-size-20" style={{ color: "#556ee6" }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{user.firstname} {user.lastname}</div>
                      <div style={{ fontSize: 12, color: "#74788d" }}>{user.email}</div>
                    </div>
                  </div>
                  {user.phone && (
                    <div className="d-flex align-items-center gap-2 text-muted font-size-13 mb-1">
                      <i className="mdi mdi-phone" /> {user.phone}
                    </div>
                  )}
                  {user.wilaya && (
                    <div className="d-flex align-items-center gap-2 text-muted font-size-13">
                      <i className="mdi mdi-map-marker" /> {user.wilaya}
                    </div>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Timeline */}
            <Card className="mb-3">
              <CardBody>
                <h5 className="mb-3">
                  <i className="mdi mdi-timeline-outline me-2 text-primary" />
                  Timeline
                </h5>
                <div className="d-flex align-items-center gap-2 text-muted font-size-13 mb-2">
                  <i className="mdi mdi-calendar-plus" /> Created: {fmtDate(visa.createdAt)}
                </div>
                <div className="d-flex align-items-center gap-2 font-size-13">
                  <i className={`${status.icon}`} style={{ color: status.color === "success" ? "#34c38f" : status.color === "danger" ? "#f46a6a" : "#f1b44c" }} />
                  Status: <Badge color={status.color}>{status.label}</Badge>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

VisaDetails.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(VisaDetails);
